import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Github,
  Linkedin,
  Twitter,
  Facebook,
  HelpCircle,
  Sparkles,
  Shield,
  FileText,
  BookOpen,
  BookMarked,
  Users,
  Trophy,
  Heart,
  MessageCircle,
} from "lucide-react";
import { DiscordIcon } from "@/components/icons/DiscordIcon";
import { ThreadsIcon } from "@/components/icons/ThreadsIcon";
import { getRootUrl, getAppUrl } from "@lyder-talk/core";
import { useTranslation } from "react-i18next";

const Footer = () => {
  const { t } = useTranslation();
  const appVersion = import.meta.env.VITE_APP_VERSION || "0.0.0";
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: DiscordIcon, href: "https://discord.gg/ACj3Ncag", label: "Discord" },
    { icon: Facebook, href: "https://www.facebook.com/lydertalk", label: "Facebook" },
    { icon: Github, href: "https://github.com/lyderdev", label: "GitHub" },
    { icon: Linkedin, href: "https://www.linkedin.com/in/l%E1%BB%A3i-v%C4%83n-645a09222/", label: "LinkedIn" },
    { icon: Twitter, href: "https://x.com/lyder_dev", label: "X (Twitter)" },
    { icon: ThreadsIcon, href: "https://www.threads.com/@loicoder", label: "Threads" },
  ];

  // Brand names ("Discord", "GitHub") stay untranslated: they are proper nouns
  // and a translated social link reads as a different service. Only the
  // descriptive copy around them is localised.
  const learnLinks = [
    { icon: BookOpen, labelKey: "footer.links.browseTopics", descriptionKey: "footer.links.browseTopicsDescription", path: "/topics" },
    { icon: BookMarked, labelKey: "footer.links.vocabulary", descriptionKey: "footer.links.vocabularyDescription", path: "/vocabulary" },
    { icon: Users, labelKey: "footer.links.studyGroups", descriptionKey: "footer.links.studyGroupsDescription", path: "/study-groups" },
    { icon: Trophy, labelKey: "footer.links.leaderboard", descriptionKey: "footer.links.leaderboardDescription", path: "/leaderboard" },
  ];

  const supportLinks = [
    { icon: Sparkles, labelKey: "footer.links.features", path: "/features" },
    { icon: HelpCircle, labelKey: "footer.links.faq", path: "/faq" },
    { icon: MessageCircle, labelKey: "footer.links.community", path: "https://www.facebook.com/lydertalk" },
  ];

  const legalLinks = [
    { icon: Shield, labelKey: "footer.links.privacy", path: "/privacy" },
    { icon: FileText, labelKey: "footer.links.terms", path: "/terms" },
  ];

  return (
    <footer className="relative z-10 border-t border-stone-200 dark:border-stone-700 bg-stone-50/80 dark:bg-stone-900 mt-auto">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">
          {/* Brand Section */}
          <div className="space-y-5 lg:col-span-1">
            <div className="flex items-center gap-3">
              <img
                src="/icon.png"
                alt="Lyder Talk"
                className="w-11 h-11 rounded-xl shadow-sm"
                width={44}
                height={44}
                loading="lazy"
              />
              <div>
                <h3 className="text-lg font-bold text-primary-strong dark:text-primary">
                  Lyder Talk
                </h3>
                <p className="text-xs text-stone-500 dark:text-stone-400 font-medium">
                  {t("footer.tagline", "Your Language Learning Platform")}
                </p>
              </div>
            </div>
            <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed">
              {t("footer.description")}
            </p>
          </div>

          {/* Learn Links */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-stone-900 dark:text-white flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-primary" />
              {t("footer.sections.learn", "Learn")}
            </h4>
            <div className="flex flex-col gap-2">
              {learnLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Button
                    key={link.path}
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(getAppUrl(link.path), "_blank", "noopener,noreferrer")}
                    className="justify-start gap-3 text-stone-600 hover:text-stone-900 hover:bg-stone-100 dark:text-stone-400 dark:hover:text-white dark:hover:bg-stone-800 transition-colors h-auto py-2"
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <div className="text-left">
                      <div className="font-medium text-sm">{t(link.labelKey)}</div>
                      <div className="text-xs text-stone-500 dark:text-stone-400">{t(link.descriptionKey)}</div>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Support Links */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-stone-900 dark:text-white flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-primary" />
              {t("footer.sections.support", "Support")}
            </h4>
            <div className="flex flex-col gap-1.5">
              {supportLinks.map((link) => {
                const Icon = link.icon;
                const isExternal = link.path.startsWith("http");
                return (
                  <Button
                    key={link.labelKey}
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      isExternal
                        ? window.open(link.path, "_blank", "noopener,noreferrer")
                        : window.open(getRootUrl(link.path), "_blank", "noopener,noreferrer")
                    }
                    className="justify-start gap-2.5 text-stone-600 hover:text-stone-900 hover:bg-stone-100 dark:text-stone-400 dark:hover:text-white dark:hover:bg-stone-800 transition-colors"
                  >
                    <Icon className="w-4 h-4" />
                    {t(link.labelKey)}
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Community Links */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-stone-900 dark:text-white flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              {t("footer.sections.community", "Community")}
            </h4>
            <div className="flex flex-wrap gap-2">
              {socialLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Button
                    key={link.label}
                    variant="outline"
                    size="icon"
                    className="w-9 h-9 border-stone-200 dark:border-stone-700 hover:bg-stone-100 dark:hover:bg-stone-800 hover:text-stone-900 dark:hover:text-white transition-colors"
                    asChild
                  >
                    <a href={link.href} target="_blank" rel="noopener noreferrer" aria-label={link.label}>
                      <Icon className="w-4 h-4" />
                    </a>
                  </Button>
                );
              })}
            </div>
            <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">
              {t("footer.communityBlurb", "Join our community of language learners and get tips, motivation, and support.")}
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-stone-200 dark:border-stone-700" />

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6">
          <div className="flex items-center gap-2 text-sm text-stone-500 dark:text-stone-400">
            <Sparkles className="w-4 h-4 text-primary" />
            <span>{t("footer.allRightsReserved", { year: currentYear, defaultValue: `© ${currentYear} Lyder Talk. All rights reserved.` })}</span>
            <span className="text-xs text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-stone-800 px-2 py-0.5 rounded-full">
              v{appVersion}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-stone-500 dark:text-stone-400">
            {legalLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className="hover:text-stone-900 dark:hover:text-white transition-colors flex items-center gap-1.5"
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t(link.labelKey)}
                </Link>
              );
            })}
            <span className="flex items-center gap-1.5">
              {t("footer.madeWith", "Made with")}
              <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500" />
              {t("footer.forLearners", "for learners")}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
